import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { CheckMarkIcon, DuplicateIcon, SendIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { getEnrollmentEmails } from '../../../api/enrollments/get-enrollment-emails'
import { registerEnrollment } from '../../../api/enrollments/register-enrollment'
import { sendEnrollmentQuotaEmail } from '../../../api/enrollments/send-quota'
import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  enrollmentChildTitleFields,
  enrolmentDuplicationFields
} from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { SendEnrollmentProposalForm } from './enrollment-form/send-enrollment-proposal-form'

type Props = {
  enrollmentId: number
}

const EnrollmentQuotationHeader = ({ enrollmentId }: Props) => {
  const { modalState, toggleModal, closeModel } = useModalManager<
    'duplicate-enrollment' | 'send-quota' | 'confirm-enrollment'
  >(['duplicate-enrollment', 'send-quota', 'confirm-enrollment'])

  const { navigate } = useNavigator()

  const { data: enrollmentEmails } = useQuery({
    queryKey: ['enrollment-emails', enrollmentId],
    queryFn: () => getEnrollmentEmails(enrollmentId)
  })

  const { data: enrollment } = useQuery({
    queryKey: ['enrollment', enrollmentId, enrollmentChildTitleFields],
    queryFn: () => getEnrollment(enrollmentId, enrollmentChildTitleFields)
  })

  const { mutate: registerEnrollmentMutation, isPending: isRegisterEnrollmentPending } =
    useMutation({
      mutationFn: registerEnrollment,
      onError: showAxiosError,
      onSuccess: () => {
        toast.success("Inscription convertie en bulletin d'inscription")
        navigate(Paths.ENROLLMENT_UPDATE_PAGE.replace(':id', enrollmentId.toString()), {
          replace: true
        })
      }
    })

  const { mutate: duplicateEnrollmentMutation, isPending: isDuplicateEnrollmentPending } =
    useMutation({
      mutationFn: () => getEnrollment(enrollmentId, enrolmentDuplicationFields),
      onError: showAxiosError,
      onSuccess: (data) => navigate(Paths.ENROLLMENT_QUOTATION_CREATE_PAGE, { state: data })
    })

  const { mutateAsync: sendEnrollmentQuotaMutation } = useMutation({
    mutationFn: sendEnrollmentQuotaEmail,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Devis envoyée')
      closeModel('send-quota')
    }
  })

  if (!enrollmentEmails || !enrollment) return null

  return (
    <div className="flex gap-3 w-full justify-end items-center">
      <Button
        type="button"
        variant={'secondary'}
        icon={DuplicateIcon}
        label="Dupliquer"
        className="w-fit"
        onClick={() => toggleModal('duplicate-enrollment')}
      />
      <Button
        type="button"
        variant={'secondary'}
        icon={SendIcon}
        label="Renvoyer le devis"
        className="w-fit"
        onClick={() => toggleModal('send-quota')}
      />
      <Button
        type="button"
        variant={'ghost'}
        label="Convertir en bulletin d'inscription"
        className="w-fit"
        onClick={() => toggleModal('confirm-enrollment')}
      />

      {/* duplicate enrollment modal */}
      <Modal
        icon={DuplicateIcon}
        variant={'confirmation'}
        isOpen={modalState['duplicate-enrollment']}
        isLoading={isDuplicateEnrollmentPending}
        title={`Êtes-vous sûr de vouloir dupliquer le formulaire d'inscription de " ${EnrollmentProperties.getEnrollmentTitle(enrollment)} " ?`}
        actionButtonsOptions={{
          confirmText: 'Dupliquer',
          cancelText: 'Fermer',
          onConfirm: () => duplicateEnrollmentMutation()
        }}
        onCancel={() => closeModel('duplicate-enrollment')}
      />

      {/* send enrollment proposal modal */}
      <Modal
        hideActions
        icon={SendIcon}
        isOpen={modalState['send-quota']}
        onCancel={() => toggleModal('send-quota')}
        title={'Renvoyer le devis'}
      >
        <SendEnrollmentProposalForm
          mode="quotation"
          enrollmentId={enrollmentId}
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ emails: enrollmentEmails }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('send-quota'),
            onConfirm: ({ emails }) => sendEnrollmentQuotaMutation({ id: enrollmentId, emails })
          })}
        />
      </Modal>

      {/* confirm enrollment modal */}
      <Modal
        icon={CheckMarkIcon}
        variant={'confirmation'}
        isOpen={modalState['confirm-enrollment']}
        onCancel={() => closeModel('confirm-enrollment')}
        title={`Voulez-vous convertir le devis de "${EnrollmentProperties.getEnrollmentTitle(enrollment)}" en bulletin d'inscription ?`}
        actionButtonsOptions={{
          cancelText: 'Fermer',
          confirmText: 'Confirmer',
          onConfirm: () => registerEnrollmentMutation(enrollmentId)
        }}
        isLoading={isRegisterEnrollmentPending}
      />
    </div>
  )
}
export default EnrollmentQuotationHeader
